/**
 * The offline write queue, in the browser.
 *
 * The machinery is `@calorio/core`'s — see `packages/core/src/offline.tsx` for
 * why every write goes through the queue whether or not there is a network.
 * This file is the two things a browser does differently: where the queue rests
 * between visits, and which events mean "the network may be back".
 *
 * The web client wrote straight through until now, which was survivable while
 * logging was one request at a time. It stopped being survivable once a single
 * action could issue three or four writes in a row, because the failure mode is
 * a half-committed basket with nothing on screen saying which half — the exact
 * situation the phone's queue was built to prevent.
 */

import {
  deviceTimeZone,
  OfflineProvider as SharedOfflineProvider,
  type QueueStorage,
} from "@calorio/core";
import { useMemo, type ReactNode } from "react";

import { useApi } from "./providers";
import { useSession } from "./session";

export { useOffline } from "@calorio/core";

/**
 * One key, versioned because the shape is persisted.
 *
 * A future change to `QueuedWrite` that cannot read the old rows should bump
 * this and abandon them rather than replay something it half-understands into
 * somebody's diary. Deliberately the same key the phone uses, so the two stay
 * legible as one format.
 */
const STORAGE_KEY = "calorio.write-queue.v1";

/**
 * `localStorage`, wrapped in the async interface the phone's storage needs.
 *
 * Every path swallows its own failure. Private-mode browsers throw on write,
 * and a quota error is not a reason to fail somebody's log entry: the write is
 * still in memory and the next save carries it. Losing the queue on a crash is
 * bad; taking the app down with it is worse.
 */
const storage: QueueStorage = {
  async load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },
  async save(queue) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch {
      /* see above */
    }
  },
};

export function OfflineProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const { status } = useSession();

  /**
   * Two signals, because neither alone is enough.
   *
   * `online` is the direct one but it lies in both directions — a laptop on a
   * captive-portal wifi is "online" with no route out, and some browsers are
   * slow to fire it after a sleep. A tab becoming visible is the browser's
   * equivalent of the phone's foregrounding, and it catches the case the event
   * misses: the machine was asleep, the network came and went, and nobody told
   * the page.
   */
  const onWake = useMemo(
    () => (wake: () => void) => {
      const onVisible = () => {
        if (document.visibilityState === "visible") wake();
      };
      window.addEventListener("online", wake);
      document.addEventListener("visibilitychange", onVisible);
      return () => {
        window.removeEventListener("online", wake);
        document.removeEventListener("visibilitychange", onVisible);
      };
    },
    [],
  );

  return (
    <SharedOfflineProvider
      api={api}
      // Provisional only: the queue reads the profile's zone from the cache at
      // the moment of each write. Asking for the profile here would fire
      // `/v1/me` on a launch heading for the sign-in screen.
      timeZone={deviceTimeZone()}
      signedIn={status === "signed-in"}
      storage={storage}
      onWake={onWake}
    >
      {children}
    </SharedOfflineProvider>
  );
}
